import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { suggestItemDisplayLabel } from '../lib/mapy.js';
import { useOnChange } from '../lib/use-on-change.js';
export function AddressAutocomplete({ value, onChange, suggest, placeholder, label, testIdPrefix, inputId, minLength = 2, debounceMs = 220, loadingLabel, emptyLabel, approximateHint, onEnterPick, mapyLogoSrc, }) {
    const [query, setQuery] = React.useState(value.query);
    const [items, setItems] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [status, setStatus] = React.useState('idle');
    const [activeIndex, setActiveIndex] = React.useState(-1);
    const wrapRef = React.useRef(null);
    const timerRef = React.useRef(null);
    const abortRef = React.useRef(null);
    // Krok FLK1: dotaz z rodice jen pri skutecne zmene, ne pri mountu — mount
    // efekt by prepsal znaky napsane pred svym dobehnutim (`useOnChange`).
    useOnChange(value.query, setQuery);
    // Krok MRGD7: pri odmontovani zrus rozdelany debounce i letici dotaz. Bez
    // toho timer dobehne nad odmontovanym korenem a `setStatus` v `runQuery`
    // sahne na React planovac — v jsdom uz po uklidu prostredi (`window is not
    // defined`), v prohlizeci zbytecny dotaz na Mapy.com po opusteni formulare.
    // Stejny vzor jako `DraftTextInput` (apps/web-admin/src/components/contracts.tsx).
    React.useEffect(() => () => {
        if (timerRef.current !== null)
            clearTimeout(timerRef.current);
        abortRef.current?.abort();
    }, []);
    React.useEffect(() => {
        if (!open)
            return;
        const onOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener('mousedown', onOutside);
        return () => {
            document.removeEventListener('mousedown', onOutside);
        };
    }, [open]);
    const runQuery = React.useCallback((q) => {
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setStatus('pending');
        void suggest(q, ctrl.signal).then((results) => {
            if (ctrl.signal.aborted)
                return;
            setItems(results);
            setActiveIndex(-1);
            setOpen(results.length > 0);
            setStatus('done');
        });
    }, [suggest]);
    const handleInput = (text) => {
        setQuery(text);
        onChange({ query: text, lat: null, lon: null, source: 'manual' });
        if (timerRef.current !== null)
            clearTimeout(timerRef.current);
        if (text.trim().length < minLength) {
            setItems([]);
            setOpen(false);
            setStatus('idle');
            return;
        }
        timerRef.current = setTimeout(() => runQuery(text.trim()), debounceMs);
    };
    const pick = (item) => {
        const picked = suggestItemDisplayLabel(item);
        setQuery(picked);
        onChange({
            query: picked,
            lat: item.position?.lat ?? null,
            lon: item.position?.lon ?? null,
            source: 'mapy',
        });
        setOpen(false);
        setStatus('idle');
    };
    const handleKeyDown = (e) => {
        if (!open || items.length === 0)
            return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, items.length - 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        }
        else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            const item = items[activeIndex];
            if (item) {
                pick(item);
                onEnterPick?.({
                    query: suggestItemDisplayLabel(item),
                    lat: item.position?.lat ?? null,
                    lon: item.position?.lon ?? null,
                    source: 'mapy',
                });
            }
        }
        else if (e.key === 'Escape') {
            setOpen(false);
        }
    };
    return (_jsxs("div", { className: "rh-suggest-wrap", ref: wrapRef, children: [_jsx("input", { type: "text", className: "rh-input", style: { width: '100%' }, value: query, onChange: (e) => handleInput(e.target.value), onKeyDown: handleKeyDown, onFocus: () => {
                    if (items.length > 0)
                        setOpen(true);
                }, placeholder: placeholder, "aria-label": label, "aria-expanded": open, "aria-autocomplete": "list", role: "combobox", "data-testid": `${testIdPrefix}-input`, ...(inputId !== undefined ? { id: inputId } : {}) }), open && items.length > 0 && approximateHint !== undefined && items.every((item) => item.approximate === true) ? (_jsx("p", { className: "rh-suggest__hint", role: "status", "data-testid": `${testIdPrefix}-suggest-approximate`, children: approximateHint })) : null, open && items.length > 0 ? (_jsxs("ul", { className: "rh-suggest", role: "listbox", "aria-label": label, "data-testid": `${testIdPrefix}-suggest`, children: [items.map((item, i) => (_jsx("li", { role: "option", "aria-selected": i === activeIndex, className: `rh-suggest__item${i === activeIndex ? ' rh-suggest__item--active' : ''}`, "data-testid": `${testIdPrefix}-suggest-item-${String(i)}`, onMouseDown: (e) => {
                            e.preventDefault();
                            pick(item);
                        }, children: suggestItemDisplayLabel(item) }, `${item.name}-${item.location}-${String(i)}`))), mapyLogoSrc !== undefined ? (_jsx("li", { className: "rh-suggest__attribution", role: "presentation", children: _jsxs("a", { href: "https://mapy.com/", target: "_blank", rel: "noreferrer", children: ["Search by ", _jsx("img", { src: mapyLogoSrc, alt: "Mapy.com", height: 14 })] }) })) : null] })) : null, !open && status === 'pending' && loadingLabel !== undefined ? (_jsx("p", { className: "rh-suggest__status", role: "status", "data-testid": `${testIdPrefix}-suggest-loading`, children: loadingLabel })) : null, !open && status === 'done' && items.length === 0 && emptyLabel !== undefined ? (_jsx("p", { className: "rh-suggest__status", role: "status", "data-testid": `${testIdPrefix}-suggest-empty`, children: emptyLabel })) : null] }));
}
//# sourceMappingURL=address-autocomplete.js.map